import { useCallback, useEffect, useMemo, useRef, useState, CSSProperties } from "react";

import { useAppState } from "../../state";

import FeatureFlagDetailsPanel, {
  FEATURE_FLAG_PANEL_WIDTH_DEFAULT_PX,
} from "./FeatureFlagDetailsPanel";
import FeatureFlagList, { FeatureFlagRowError } from "./FeatureFlagList";

import {
  Button,
  Card,
  Modal,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";

import FeatureFlag from "./FeatureFlag";
import {
  SessionExpiredError,
  applyConditionalFeatureFlagTenant,
  getFeatureFlags,
  removeConditionalFeatureFlagFacility,
  removeConditionalFeatureFlagTenant,
  removeFacilityFeatureFlag,
  removeTenantFeatureFlag,
  setTenantFeatureFlags,
} from "./FeatureFlagApi";

/** Confirm-then-POST payload for one tenant-scoped Enabled toggle. */
interface PendingEnabledToggle {
  id: number;
  name: string;
  enabled: boolean;
}

/**
 * Confirm-then-DELETE payload for one assignment removal:
 * - "tenantRow": the flag<=>tenant row (Tenants tab).
 * - "facilityRow": one flag<=>facility row (Facilities tab).
 * - "flagForTenant": everything tying the flag to the selected tenant — the
 *   tenant row and all of the tenant's facility rows (main-table remove
 *   control and the panel's "Remove from tenant" button).
 */
type PendingRemoval =
  | { kind: "tenantRow"; flagId: number; flagName: string; tenantName: string }
  | {
      kind: "facilityRow";
      flagId: number;
      flagName: string;
      facilityId: number;
      facilityName: string;
    }
  | {
      kind: "flagForTenant";
      flagId: number;
      flagName: string;
      tenantName: string;
      hasTenantRow: boolean;
      facilityCount: number;
    };

interface ToggleFeatureFlagsPageProps {
  /** Tenant selected in the left-rail list; scopes which flags are listed. */
  selectedTenantId: number;
}

/** Full-page navigation (fetch cannot follow the OIDC redirects) that round-trips
 * through Entra to reissue the Platform Core token, then returns to this page. */
function navigateToReauthenticate() {
  const returnUrl = window.location.pathname + window.location.search;
  window.location.href = `Reauthenticate?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export default function ToggleFeatureFlagsPage({
  selectedTenantId,
}: ToggleFeatureFlagsPageProps) {
  const [isLoading, setIsLoading] = useState(true);

  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);

  // Flag whose details panel is open; clicking its row again closes it. The
  // panel renders from the flags list, so it disappears automatically when
  // the flag is deleted or removed.
  const [selectedFlagId, setSelectedFlagId] = useState<number | null>(null);
  const [detailsPanelWidthPx, setDetailsPanelWidthPx] = useState(
    FEATURE_FLAG_PANEL_WIDTH_DEFAULT_PX
  );

  const [showAddFlagModal, setShowAddFlagModal] = useState(false);
  const [showEditFlagModal, setShowEditFlagModal] = useState(false);
  const [listInfoExpanded, setListInfoExpanded] = useState(true);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // Flag id -> error message from the last delete attempt. Errored rows stay
  // highlighted until reset or removed.
  const [flagErrors, setFlagErrors] = useState<Map<number, string>>(new Map());
  const [showErrorModal, setShowErrorModal] = useState(false);

  const originalFlagsRef = useRef<Map<number, FeatureFlag>>(new Map());
  const featureFlagsRef = useRef<FeatureFlag[]>([]);
  const pendingToggleRef = useRef<PendingEnabledToggle | null>(null);
  const [pendingToggle, setPendingToggle] = useState<PendingEnabledToggle | null>(null);
  const [isToggleSaving, setIsToggleSaving] = useState(false);
  const pendingRemovalRef = useRef<PendingRemoval | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [isRemovalSaving, setIsRemovalSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { onAppStateRefresh } = useAppState();
  featureFlagsRef.current = featureFlags;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setIsSessionExpired(false);
    setSelectedFlagId(null);
    pendingToggleRef.current = null;
    setPendingToggle(null);
    pendingRemovalRef.current = null;
    setPendingRemoval(null);
    setFeatureFlags([]);

    getFeatureFlags(selectedTenantId)
      .then(data => {
        if (cancelled) {
          return;
        }
        originalFlagsRef.current = new Map(data.map(flag => [flag.id, flag]));
        setFeatureFlags(data);
      })
      .catch(e => {
        if (cancelled) {
          return;
        }
        console.log(e);
        if (e instanceof SessionExpiredError) {
          setIsSessionExpired(true);
        } else {
          setLoadError(e instanceof Error && e.message ? e.message : "Error loading feature flags");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  const onSelectFlag = useCallback((id: number) => {
    setSelectedFlagId((current) => (current === id ? null : id));
  }, []);

  /** Closes the detail panel */
  const onCloseDetailsPanel = useCallback(() => { setSelectedFlagId(null); }, []);

  // Clicks inside a modal (toggle confirmation, error acknowledgement, ...) are
  // part of the panel's flow even though modals render outside the panel DOM,
  // so they must not close the panel.
  const onClickOutsidePanel = useCallback((event: MouseEvent | TouchEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(".modal")) {
      return;
    }
    setSelectedFlagId(null);
  }, []);

  const onToggleEnabled = useCallback((id: number, enabled: boolean) => {
    if (pendingToggleRef.current) {
      return;
    }

    const flag = featureFlagsRef.current.find((item) => item.id === id);
    // SetTenantFeatureFlags upserts a ConditionalFeatureFlagTenant row.
    // Facility-only flags have none; posting would create a tenant-wide
    // assignment (and Platform Core ignores enabled:false on insert).
    if (!flag || flag.enabled === enabled || flag.conditionalFeatureFlagTenantId == null) {
      return;
    }

    const pending: PendingEnabledToggle = {
      id,
      name: flag.name,
      enabled,
    };
    pendingToggleRef.current = pending;
    setPendingToggle(pending);
  }, []);

  const onCancelPendingToggle = useCallback(() => {
    if (isToggleSaving) {
      return;
    }

    pendingToggleRef.current = null;
    setPendingToggle(null);
  }, [isToggleSaving]);

  const onConfirmPendingToggle = useCallback(async () => {
    const pending = pendingToggleRef.current;
    if (!pending || isToggleSaving) {
      return;
    }

    setIsToggleSaving(true);
    try {
      // The POST returns the updated ConditionalFeatureFlagTenant row; apply
      // its dates (and derived enabled state) onto the local flag.
      const updatedTenantRow = await setTenantFeatureFlags(
        pending.id,
        selectedTenantId,
        pending.enabled
      );
      setFeatureFlags((current) =>
        current.map((flag) =>
          flag.id === pending.id
            ? applyConditionalFeatureFlagTenant(flag, updatedTenantRow)
            : flag
        )
      );
      const original = originalFlagsRef.current.get(pending.id);
      if (original) {
        originalFlagsRef.current.set(
          pending.id,
          applyConditionalFeatureFlagTenant(original, updatedTenantRow)
        );
      }
      pendingToggleRef.current = null;
      setPendingToggle(null);
      onAppStateRefresh();
    } catch (e) {
      pendingToggleRef.current = null;
      setPendingToggle(null);
      if (e instanceof SessionExpiredError) {
        setIsSessionExpired(true);
        return;
      }
      setActionError(
        e instanceof Error && e.message
          ? e.message
          : `Failed to update feature flag ${pending.id}`
      );
    } finally {
      setIsToggleSaving(false);
    }
  }, [isToggleSaving, onAppStateRefresh, selectedTenantId]);

  const clearFlagState = useCallback((id: number) => {
    setFlagErrors((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);

  const onDeleteTenantRow = useCallback((flagId: number) => {
    if (pendingRemovalRef.current) {
      return;
    }

    const flag = featureFlagsRef.current.find((item) => item.id === flagId);
    // A facility-only flag has no ConditionalFeatureFlagTenant row to delete.
    if (!flag || flag.conditionalFeatureFlagTenantId == null) {
      return;
    }

    const pending: PendingRemoval = {
      kind: "tenantRow",
      flagId,
      flagName: flag.name,
      tenantName: flag.tenantName,
    };
    pendingRemovalRef.current = pending;
    setPendingRemoval(pending);
  }, []);

  const onDeleteFacilityRow = useCallback((flagId: number, facilityId: number) => {
    if (pendingRemovalRef.current) {
      return;
    }

    const flag = featureFlagsRef.current.find((item) => item.id === flagId);
    const facility = flag?.facilities.find((item) => item.facilityId === facilityId);
    if (!flag || !facility) {
      return;
    }

    const pending: PendingRemoval = {
      kind: "facilityRow",
      flagId,
      flagName: flag.name,
      facilityId,
      facilityName: facility.facilityName,
    };
    pendingRemovalRef.current = pending;
    setPendingRemoval(pending);
  }, []);

  const onDeleteFlagForTenant = useCallback((flagId: number) => {
    if (pendingRemovalRef.current) {
      return;
    }

    const flag = featureFlagsRef.current.find((item) => item.id === flagId);
    if (!flag) {
      return;
    }

    const pending: PendingRemoval = {
      kind: "flagForTenant",
      flagId,
      flagName: flag.name,
      tenantName: flag.tenantName,
      hasTenantRow: flag.conditionalFeatureFlagTenantId != null,
      facilityCount: flag.facilities.length,
    };
    pendingRemovalRef.current = pending;
    setPendingRemoval(pending);
  }, []);

  const onCancelPendingRemoval = useCallback(() => {
    if (isRemovalSaving) {
      return;
    }

    pendingRemovalRef.current = null;
    setPendingRemoval(null);
  }, [isRemovalSaving]);

  /** Replaces a flag in the list, dropping it when `next` is null. */
  const replaceFlag = useCallback(
    (flagId: number, next: (flag: FeatureFlag) => FeatureFlag | null) => {
      setFeatureFlags((current) =>
        current.flatMap((flag) => {
          if (flag.id !== flagId) {
            return [flag];
          }
          const remaining = next(flag);
          return remaining ? [remaining] : [];
        })
      );
      const original = originalFlagsRef.current.get(flagId);
      const remainingOriginal = original ? next(original) : null;
      if (remainingOriginal) {
        originalFlagsRef.current.set(flagId, remainingOriginal);
      } else {
        originalFlagsRef.current.delete(flagId);
      }
    },
    []
  );

  const onConfirmPendingRemoval = useCallback(async () => {
    const pending = pendingRemovalRef.current;
    if (!pending || isRemovalSaving) {
      return;
    }

    setIsRemovalSaving(true);
    try {
      if (pending.kind === "tenantRow") {
        await removeTenantFeatureFlag(pending.flagId, selectedTenantId);
        // Mirror what GetForTenant would now return: facility-only when
        // facility rows remain, gone from the tenant's list otherwise (the
        // details panel follows the list, so it closes with the flag).
        replaceFlag(pending.flagId, removeConditionalFeatureFlagTenant);
      } else if (pending.kind === "facilityRow") {
        await removeFacilityFeatureFlag(pending.flagId, pending.facilityId);
        replaceFlag(pending.flagId, (flag) =>
          removeConditionalFeatureFlagFacility(flag, pending.facilityId)
        );
      } else {
        // Remove everything tying the flag to the selected tenant: the
        // tenant row (if any) and each of the tenant's facility rows.
        const flag = featureFlagsRef.current.find(
          (item) => item.id === pending.flagId
        );
        if (flag) {
          if (flag.conditionalFeatureFlagTenantId != null) {
            await removeTenantFeatureFlag(pending.flagId, selectedTenantId);
          }
          for (const facility of flag.facilities) {
            await removeFacilityFeatureFlag(pending.flagId, facility.facilityId);
          }
        }
        setFeatureFlags((current) =>
          current.filter((item) => item.id !== pending.flagId)
        );
        originalFlagsRef.current.delete(pending.flagId);
        clearFlagState(pending.flagId);
      }

      pendingRemovalRef.current = null;
      setPendingRemoval(null);
      onAppStateRefresh();
    } catch (e) {
      pendingRemovalRef.current = null;
      setPendingRemoval(null);
      if (e instanceof SessionExpiredError) {
        setIsSessionExpired(true);
        return;
      }
      setActionError(
        e instanceof Error && e.message
          ? e.message
          : `Failed to remove feature flag ${pending.flagId}`
      );
      if (pending.kind === "flagForTenant") {
        // The batch may have partially applied (e.g. tenant row removed but
        // a facility delete failed); reload so the list matches the
        // database instead of guessing.
        try {
          const data = await getFeatureFlags(selectedTenantId);
          originalFlagsRef.current = new Map(data.map((flag) => [flag.id, flag]));
          setFeatureFlags(data);
        } catch {
          // Keep the current list; the error modal already tells the user
          // the removal did not fully complete.
        }
      }
    } finally {
      setIsRemovalSaving(false);
    }
  }, [
    clearFlagState,
    isRemovalSaving,
    onAppStateRefresh,
    replaceFlag,
    selectedTenantId,
  ]);

  /** Reverts an existing errored row to the values loaded from the server. */
  const onResetFlag = useCallback((id: number) => {
    const original = originalFlagsRef.current.get(id);
    if (original) {
      setFeatureFlags((current) =>
        current.map((flag) => (flag.id === id ? original : flag))
      );
    }
    clearFlagState(id);
  }, [clearFlagState]);

  /** Drops a new errored row that never made it to the server. */
  const onRemoveFlag = useCallback((id: number) => {
    setFeatureFlags((current) => current.filter((flag) => flag.id !== id));
    clearFlagState(id);
  }, [clearFlagState]);

  const selectedFlag =
    featureFlags.find((flag) => flag.id === selectedFlagId) ?? null;

  const removalModalTitle =
    pendingRemoval?.kind === "facilityRow"
      ? "Confirm facility removal"
      : pendingRemoval?.kind === "flagForTenant"
        ? "Confirm removal from tenant"
        : "Confirm tenant removal";

  // Row error info for the list: which rows to highlight and whether the row is
  // a new (unsaved) item, which decides between the Reset and Remove actions.
  // Memoized so the map identity is stable across unrelated re-renders; the
  // list rebuilds its columns (remounting every cell) when this changes.
  const rowErrors = useMemo(
    () =>
      new Map<number, FeatureFlagRowError>(
        Array.from(flagErrors, ([id, message]) => [
          id,
          { message, isNew: !originalFlagsRef.current.has(id) },
        ])
      ),
    [flagErrors]
  );

  const pageTitleContent = (
    <Card.Header className="flagListInfoHeader">
      <button
        type="button"
        className="flagListInfoToggle"
        aria-expanded={listInfoExpanded}
        aria-controls="flagListInfoBody"
        aria-label={
          listInfoExpanded
            ? "Hide feature flag usage notes"
            : "Show feature flag usage notes"
        }
        onClick={() => setListInfoExpanded((open) => !open)}
      >
        <i
          className={`fa ${listInfoExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}
          aria-hidden="true"
        />
        <i className="fa fa-info-circle" aria-hidden="true" />
        <span className="flagListInfoLabel">Usage notes</span>
      </button>
      {listInfoExpanded && (
        <div className="flagListInfoBody">
          <p>
            Enable one or more feature flag(s) by turning on and off the switch near
            each flag.
          </p>
          <p>
            Note: Changing the feature flags will require you to log out from the
            application, close the browser window and all tabs, then open a new
            browser window for the new setting to get in effect.
          </p>
        </div>

      )}
    </Card.Header>
  );

  return (
    <Card className="table-with-switches bg-white border-0 flagCard" id="tableCard">
      {pageTitleContent}
      <Card.Body>
        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" aria-label="Loading feature flags" />
          </div>
        ) : isSessionExpired ? (
          <div className="d-flex flex-column align-items-center py-4">
            <label className="error">
              Your session with Platform Core has expired, so the feature flags could not be loaded.
            </label>
            <Button variant="primary" type="button" onClick={navigateToReauthenticate}>
              <i className="fa fa-refresh"></i>
              {" "}Re-authenticate
            </Button>
          </div>
        ) : loadError ? (
          <div className="d-flex justify-content-center py-4">
            <label className="error">{loadError}</label>
          </div>
        ) : (
          // Details panel overlays the list (same grid cell). The table keeps
          // its full-width column model; the panel paints over the right side.
          <div
            className={`featureFlagListLayout${selectedFlag ? " hasDetailsPanel" : ""}`}
            style={
              selectedFlag
                ? ({ "--feature-flag-panel-width": `${detailsPanelWidthPx}px` } as CSSProperties)
                : undefined
            }
          >
            <div className="featureFlagMain">
              <FeatureFlagList
                featureFlags={featureFlags}
                pendingToggle={pendingToggle}
                rowErrors={rowErrors}
                selectedFlagId={selectedFlagId}
                onSelectFlag={onSelectFlag}
                onToggleEnabled={onToggleEnabled}
                onResetFlag={onResetFlag}
                onRemoveFlag={onRemoveFlag}
                onDeleteFlagForTenant={onDeleteFlagForTenant}
              />
              <Row className="featureFlagMainActionsRow">
                <Col className="d-flex finalColumn featureFlagMainActions">
                  <Button
                    className="mr-1"
                    variant="primary"
                    type="button"
                    onClick={() => setShowAddFlagModal(true)}
                  >
                    <i className="fa fa-plus"></i>
                    {" "}Add Flag
                  </Button>
                </Col>
              </Row>
            </div>
            {selectedFlag && (
              <FeatureFlagDetailsPanel
                flag={selectedFlag}
                pendingEnabled={
                  pendingToggle?.id === selectedFlag.id ? pendingToggle.enabled : null
                }
                onToggleEnabled={onToggleEnabled}
                onEditFlag={() => setShowEditFlagModal(true)}
                onDeleteFlagForTenant={onDeleteFlagForTenant}
                onDeleteTenantRow={onDeleteTenantRow}
                onDeleteFacilityRow={onDeleteFacilityRow}
                onClose={onCloseDetailsPanel}
                widthPx={detailsPanelWidthPx}
                onWidthChange={setDetailsPanelWidthPx}
                onOutsideCallback={onClickOutsidePanel}
              />
            )}
          </div>
        )}

        <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Feature Flag Errors</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              The following feature flags could not be deleted. Their rows are
              highlighted in red; use Reset to clear the error, or try the
              delete again.
            </p>
            <ul className="mb-0">
              {Array.from(flagErrors, ([id, message]) => {
                const flag = featureFlags.find(f => f.id === id);
                return (
                  <li key={id}>
                    <strong>{flag ? flag.name : `Flag ${id}`}</strong>: {message}
                  </li>
                );
              })}
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowErrorModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={pendingToggle != null}
          onHide={onCancelPendingToggle}
          centered
          aria-label="Confirm feature flag change"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm feature flag change</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {pendingToggle && (
              <p>
                {pendingToggle.enabled ? "Enable" : "Disable"}{" "}
                <strong>{pendingToggle.name}</strong>?
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={onCancelPendingToggle}
              disabled={isToggleSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={onConfirmPendingToggle}
              disabled={isToggleSaving}
            >
              {isToggleSaving ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span> Updating... </span>
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={pendingRemoval != null}
          onHide={onCancelPendingRemoval}
          centered
          aria-label={removalModalTitle}
        >
          <Modal.Header closeButton>
            <Modal.Title>{removalModalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {pendingRemoval?.kind === "tenantRow" && (
              <p>
                Remove <strong>{pendingRemoval.tenantName}</strong> from{" "}
                <strong>{pendingRemoval.flagName}</strong>? The flag will no
                longer apply to this tenant; facility assignments are not
                affected.
              </p>
            )}
            {pendingRemoval?.kind === "facilityRow" && (
              <p>
                Remove <strong>{pendingRemoval.facilityName}</strong> from{" "}
                <strong>{pendingRemoval.flagName}</strong>? The flag will no
                longer apply to this facility.
              </p>
            )}
            {pendingRemoval?.kind === "flagForTenant" && (
              <p>
                Remove <strong>{pendingRemoval.flagName}</strong> from{" "}
                <strong>{pendingRemoval.tenantName}</strong>? This removes{" "}
                {pendingRemoval.hasTenantRow && "the tenant assignment"}
                {pendingRemoval.hasTenantRow &&
                  pendingRemoval.facilityCount > 0 &&
                  " and "}
                {pendingRemoval.facilityCount > 0 && (
                  <>
                    all {pendingRemoval.facilityCount} facility{" "}
                    {pendingRemoval.facilityCount === 1
                      ? "assignment"
                      : "assignments"}
                  </>
                )}{" "}
                for this tenant. The flag definition is not deleted.
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={onCancelPendingRemoval}
              disabled={isRemovalSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={onConfirmPendingRemoval}
              disabled={isRemovalSaving}
            >
              {isRemovalSaving ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span> Removing... </span>
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={actionError != null}
          onHide={() => setActionError(null)}
          centered
          aria-label="Feature Flag Error"
        >
          <Modal.Header closeButton>
            <Modal.Title>Feature Flag Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{actionError}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" type="button" onClick={() => setActionError(null)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showAddFlagModal} onHide={() => setShowAddFlagModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Flag</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Adding feature flags is not available yet.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowAddFlagModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEditFlagModal} onHide={() => setShowEditFlagModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Flag</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Editing feature flags is not available yet.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowEditFlagModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

      </Card.Body>
    </Card>
  );
}
